import { logger } from '../lib/logger';
import React, { useRef, useState, useEffect } from 'react';
import { toPng } from 'html-to-image';
import { Download, Share2, MessageCircle } from 'lucide-react';

const LOGO_URL = '/logo-wing-white.png';
const SITE_URL = 'https://wingacademybrasil.com.br';

// Card no formato 3:4 (Stories) — 540x720 base, exportado em 1080x1440
const CARD_W = 540;
const CARD_H = 720;

const getGrade = (percentual) => {
  if (percentual >= 90) return { text: 'EXCELENTE', emoji: '🏆' };
  if (percentual >= 70) return { text: 'MUITO BOM', emoji: '⭐' };
  if (percentual >= 50) return { text: 'BOM', emoji: '👍' };
  return { text: 'EM EVOLUÇÃO', emoji: '📚' };
};

// Converte uma URL de imagem para data URL (base64).
// Garante que a logo apareça no PNG exportado mesmo se houver problema de cache/CORS.
const fetchAsDataUrl = async (url) => {
  try {
    const res = await fetch(url, { cache: 'force-cache' });
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (e) {
    logger.warn('Falha ao converter logo em base64:', e);
    return url; // fallback
  }
};

const QuizShareCard = ({ nome, pontuacao, total, percentual }) => {
  const cardRef = useRef(null);
  const wrapperRef = useRef(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [scale, setScale] = useState(1);
  const [logoSrc, setLogoSrc] = useState(LOGO_URL);

  const grade = getGrade(percentual);
  const firstName = (() => {
    const parts = (nome || 'Aluno').trim().split(/\s+/);
    // Mantém Nome + primeiro sobrenome (se houver) — separados por espaço.
    return parts.slice(0, 2).join(' ').toUpperCase();
  })();
  const shareText = `Acabei de fazer o Quiz da WING Academy Brasil e acertei ${pontuacao} de ${total} (${percentual.toFixed(0)}%) — ${grade.text}! 🎚️\nFaça você também: ${SITE_URL}/quiz`;
  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareText)}`;

  // Pré-carrega a logo como base64 para embutir no PNG exportado
  useEffect(() => {
    let cancelled = false;
    fetchAsDataUrl(LOGO_URL).then((dataUrl) => {
      if (!cancelled) setLogoSrc(dataUrl);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, []);

  // Escala o card visualmente para caber em qualquer tela (mobile inclusive)
  useEffect(() => {
    const updateScale = () => {
      if (!wrapperRef.current) return;
      const w = wrapperRef.current.offsetWidth;
      setScale(Math.min(1, w / CARD_W));
    };
    updateScale();
    window.addEventListener('resize', updateScale);
    return () => window.removeEventListener('resize', updateScale);
    // eslint-disable-next-line
  }, []);

  const generateImage = async () => {
    if (!cardRef.current) return null;

    // Aguarda a logo estar pronta como base64. Se o usuário clicar antes do
    // useEffect terminar o fetch, fazemos o fetch agora (e dá um pequeno tempo
    // para o React re-renderizar o <img src=base64> antes de capturar).
    if (!logoSrc.startsWith('data:')) {
      const dataUrl = await fetchAsDataUrl(LOGO_URL);
      if (dataUrl.startsWith('data:')) {
        setLogoSrc(dataUrl);
        // Espera 1 frame para o React atualizar o DOM com a logo em base64
        await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      }
    }

    return await toPng(cardRef.current, {
      cacheBust: true,
      pixelRatio: 2, // 540x720 * 2 = 1080x1440
      backgroundColor: '#0a0a0a',
      width: CARD_W,
      height: CARD_H,
      style: {
        transform: 'scale(1)',
        transformOrigin: 'top left',
      },
    });
  };

  const handleDownload = async () => {
    setIsGenerating(true);
    try {
      const dataUrl = await generateImage();
      if (!dataUrl) return;
      const link = document.createElement('a');
      link.download = `wing-academy-quiz-${firstName.toLowerCase()}.png`;
      link.href = dataUrl;
      link.click();
    } catch (err) {
      logger.error('Erro ao gerar imagem:', err);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleNativeShare = async () => {
    setIsGenerating(true);
    try {
      const dataUrl = await generateImage();
      if (!dataUrl) return;
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], 'wing-academy-quiz.png', { type: 'image/png' });

      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          title: 'Meu resultado no Quiz WING Academy',
          text: shareText,
          files: [file],
        });
      } else {
        await handleDownload();
      }
    } catch (err) {
      if (err.name !== 'AbortError') logger.error('Erro ao compartilhar:', err);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="glass-dark p-6 rounded-2xl border border-yellow-500/20 mb-8" data-testid="quiz-share-section">
      <h2 className="text-xl font-bold text-white mb-2 text-center">
        Compartilhe seu resultado!
      </h2>
      <p className="text-gray-400 text-sm text-center mb-6">
        Imagem 1080×1440 — perfeita para Stories e feed
      </p>

      {/* Card visual (preview) */}
      <div
        ref={wrapperRef}
        className="mb-6 mx-auto"
        style={{ width: '100%', maxWidth: `${CARD_W}px` }}
      >
        <div
          style={{
            width: '100%',
            paddingTop: `${(CARD_H / CARD_W) * 100}%`, // 3:4
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <div
            ref={cardRef}
            data-testid="quiz-share-card"
            style={{
              width: `${CARD_W}px`,
              height: `${CARD_H}px`,
              transform: `scale(${scale})`,
              transformOrigin: 'top left',
              background:
                'radial-gradient(circle at 20% 0%, #1f1a05 0%, #0a0a0a 55%, #000000 100%)',
              position: 'absolute',
              top: 0,
              left: 0,
              fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
              color: '#fff',
              padding: '48px 40px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              borderRadius: '16px',
              border: '1px solid rgba(250, 204, 21, 0.25)',
            }}
          >
            {/* Glow accents */}
            <div
              style={{
                position: 'absolute',
                top: '-140px',
                right: '-140px',
                width: '380px',
                height: '380px',
                background: 'radial-gradient(circle, rgba(250,204,21,0.28) 0%, transparent 70%)',
                pointerEvents: 'none',
              }}
            />
            <div
              style={{
                position: 'absolute',
                bottom: '-120px',
                left: '-120px',
                width: '320px',
                height: '320px',
                background: 'radial-gradient(circle, rgba(34,197,94,0.18) 0%, transparent 70%)',
                pointerEvents: 'none',
              }}
            />

            {/* Top: Logo + tag */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                position: 'relative',
                zIndex: 1,
              }}
            >
              <img
                src={logoSrc}
                alt="WING Academy Brasil"
                crossOrigin="anonymous"
                style={{ height: '64px', width: 'auto', display: 'block' }}
              />
              <div
                style={{
                  fontSize: '11px',
                  fontWeight: 700,
                  letterSpacing: '2px',
                  padding: '6px 14px',
                  background: 'rgba(250,204,21,0.15)',
                  color: '#facc15',
                  borderRadius: '999px',
                  border: '1px solid rgba(250,204,21,0.4)',
                }}
              >
                QUIZ WING
              </div>
            </div>

            {/* Middle: Score */}
            <div style={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
              <div
                style={{
                  fontSize: '14px',
                  color: '#9ca3af',
                  letterSpacing: '3px',
                  fontWeight: 600,
                  marginBottom: '12px',
                }}
              >
                MINHA PONTUAÇÃO
              </div>
              <div
                style={{
                  fontSize: '180px',
                  fontWeight: 900,
                  lineHeight: 1,
                  background: 'linear-gradient(135deg, #facc15 0%, #f59e0b 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  marginBottom: '12px',
                }}
              >
                {pontuacao}
                <span style={{ fontSize: '78px', color: '#6b7280', WebkitTextFillColor: '#6b7280' }}>
                  /{total}
                </span>
              </div>
              <div
                style={{
                  fontSize: '24px',
                  color: '#fff',
                  fontWeight: 600,
                  marginBottom: '20px',
                }}
              >
                {percentual.toFixed(0)}% de acertos
              </div>
              <div
                style={{
                  display: 'inline-block',
                  padding: '10px 24px',
                  background: 'rgba(34,197,94,0.15)',
                  border: '1px solid rgba(34,197,94,0.5)',
                  borderRadius: '999px',
                  fontSize: '15px',
                  fontWeight: 700,
                  color: '#4ade80',
                  letterSpacing: '2px',
                }}
              >
                {grade.emoji} {grade.text}
              </div>
            </div>

            {/* Bottom: Aluno + site */}
            <div style={{ position: 'relative', zIndex: 1 }}>
              <div
                style={{
                  paddingTop: '24px',
                  borderTop: '1px solid rgba(255,255,255,0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '16px',
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: '26px',
                      fontWeight: 800,
                      color: '#fff',
                      maxWidth: '320px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {firstName}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div
                    style={{
                      fontSize: '11px',
                      color: '#6b7280',
                      letterSpacing: '2px',
                      marginBottom: '4px',
                    }}
                  >
                    TESTE VOCÊ
                  </div>
                  <div style={{ fontSize: '15px', fontWeight: 700, color: '#facc15' }}>
                    wingacademybrasil.com.br
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Botões */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <button
          onClick={handleNativeShare}
          disabled={isGenerating}
          data-testid="share-native-btn"
          className="flex items-center justify-center gap-2 bg-yellow-500 hover:bg-yellow-600 text-black font-bold px-4 py-3 rounded-lg transition-all disabled:opacity-50"
        >
          <Share2 className="w-5 h-5" />
          {isGenerating ? 'Gerando...' : 'Compartilhar'}
        </button>

        <a
          href={whatsappUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="share-whatsapp-btn"
          className="flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white font-bold px-4 py-3 rounded-lg transition-all"
        >
          <MessageCircle className="w-5 h-5" />
          WhatsApp
        </a>

        <button
          onClick={handleDownload}
          disabled={isGenerating}
          data-testid="share-download-btn"
          className="flex items-center justify-center gap-2 bg-gray-800 hover:bg-gray-700 text-white font-bold px-4 py-3 rounded-lg transition-all disabled:opacity-50"
        >
          <Download className="w-5 h-5" />
          Baixar Imagem
        </button>
      </div>

      <p className="text-[11px] text-gray-500 text-center mt-3">
        Dica: no celular, "Compartilhar" abre as opções nativas (Instagram Stories, Status, etc).
      </p>
    </div>
  );
};

export default QuizShareCard;
